import client_lookups from '@/assets/json/clientLookups.json'
import i18n from '@/i18n/i18n'
import router from '@/router'
import axiosInstance from '@/services'
import { useFormStore } from '@/stores/form'
import { useGlobalStore } from '@/stores/global'
import { useLookupsStore } from '@/stores/lookups'
import { useAuthStore } from '@/stores/auth'

const parseOptions = (optionsData, config) => {
  if ((optionsData ?? []).length === 0) return []
  if (!Object.keys(config).includes('structure')) {
    return optionsData
  } else {
    const { label, value } = config.structure
    if (label == 'diallingCode - localizedName') {
      Object.assign(
        optionsData,
        optionsData.map((o) => {
          return {
            ...o,
            label: `${o.diallingCode} - ${o.localizedName}`
          }
        })
      )
    } else {
      // otherwise set it normally
      Object.assign(
        optionsData,
        optionsData.map((o) => {
          return {
            ...o,
            label: o[label]
          }
        })
      )
    }

    Object.assign(
      optionsData,
      optionsData.map((o) => {
        return {
          ...o,
          value: o[value]
        }
      })
    )

    return optionsData
  }
}

// Process value from dynamic fields, then pass to query of the api.
const processQuery = (query) => {
  if (!query) return
  const formStore = useFormStore()
  const newQuery = {}
  if (Object.keys(query).length > 0) {
    Object.keys(query).map((key) => {
      if (query[key] == '__serviceTransactionId__') {
        newQuery[key] = router.currentRoute.value.params.serviceId
        return
      }

      if (query[key] == '__requestNumber__') {
        newQuery[key] = router.currentRoute.value.query?.requestNumber ?? null
        return
      }

      if (query[key].ref) {
        newQuery[key] = formStore.extraMap[query[key].ref] || formStore.form.values[query[key].ref]
      } else {
        newQuery[key] = query[key]
      }
    })
  }
  return newQuery
}

const handlePortId = () => {
  const {
    query: { administrativeRegionId }
  } = router.currentRoute.value

  const {
    adminRegions: { ain, dhafra }
  } = client_lookups

  const portEmirateId = [ain, dhafra].includes(administrativeRegionId) ? 1 : administrativeRegionId
  return `/${portEmirateId}/port`
}

// Process value from dynamic fields, then replace the patch with these.
const processPathQuery = (queryPath) => {
  if (!queryPath) return
  const formStore = useFormStore()

  let newQueryPath = ''
  if (Object.keys(queryPath).length > 0) {
    Object.keys(queryPath).map((key) => {
      if (queryPath[key].ref) {
        newQueryPath += `/${formStore.extraMap[queryPath[key].ref]}`
      } else if (key == 'serviceTransactionId') {
        newQueryPath += `/${useGlobalStore().startedService.serviceTransactionId}`
      } else if (key == 'portEmirateId') {
        newQueryPath += handlePortId()
      } else {
        newQueryPath += queryPath[key]
      }
    })
  }
  return newQueryPath
}

const fetchOptions = async (options, props) => {
  const formStore = useFormStore()
  const lookupStore = useLookupsStore()

  // check if options is already an array. if so, pass it
  if (Array.isArray(options.value) && options.value.length > 0) {
    return options.value.map((o) => {
      return {
        ...o,
        label: i18n.global.t(o.localizedName || o.text),
        value: o.id
      }
    })
  }

  // otherwise call the api
  const { path, query, body } = options.api
  let newPath = path
  const reactiveValue =
    formStore.form.values[props?.dependentOn] || formStore.draft.request[props?.dependentOn]
  if (reactiveValue && newPath.includes('__reactive__')) {
    newPath = newPath.replaceAll('__reactive__', reactiveValue)
  }

  /**
   * logic handler for security deposit dropdown
   * for form 417
   */
  if (newPath == 'getSecurityDepositValues') {
    const values = [1025, 2025, 3025, 5025]

    if ('mustPayDeposit' in formStore.extraMap && formStore.extraMap.mustPayDeposit) {
      values.push(2525)
    }

    return values
      .map((v) => {
        return {
          label: v,
          value: v
        }
      })
      .sort((a, b) => a.value - b.value)
  }

  if (newPath == '/api/lookup/professionsAutoComplete') {
    await lookupStore.getProfessions()
    return lookupStore.professions
  }

  if (newPath == '/api/icaRequest/draft/getResidencyAndUnifiedFormNumberOfYearsDetails') {
    router.currentRoute.value.params.serviceId
    const params = {
      serviceTransactionId: router.currentRoute.value.params.serviceId,
      requestNumber: router.currentRoute.value.query?.requestNumber
    }
    const numberOfYearsResponse = await axiosInstance.get(newPath, { params })
    const {
      numberOfYearsList,
      isServiceWithVirtualSponsorFlag,
      isNumberOfYearsDisabled,
      selectedNumberOfYears = null
    } = numberOfYearsResponse

    const formStore = useFormStore()

    const {
      residencyAndUnifiedFormServicesForCitizenSponsor,
      numberOfYears: { threeYear, fourYear },
      relationships: { son, daughter }
    } = client_lookups
    const isInService = residencyAndUnifiedFormServicesForCitizenSponsor.includes(
      router.currentRoute.value.params.serviceId
    )
    const is3or4 = numberOfYearsList.some((y) => [threeYear, fourYear].includes(y))
    const isSonOrDaughter = [son, daughter].includes(formStore.form.values.sponsorRelationshipId)

    Object.assign(formStore.extraMap, {
      isServiceWithVirtualSponsorFlag,
      isNumberOfYearsDisabled: isNumberOfYearsDisabled || (isInService && is3or4 && isSonOrDaughter)
    })
    if (useAuthStore().user.applicationId == 2 && !formStore?.draft?.requestNumber) {
      Object.assign(formStore.draft.request, {
        numberOfYears: selectedNumberOfYears
      })
    }

    return numberOfYearsList.map((v) => ({ label: v, value: v }))
  }

  if (options.api.path_query) {
    newPath += processPathQuery(options.api.path_query)
  }

  const newQuery = processQuery(query)
  const newData = processQuery(body)

  try {
    const response = await lookupStore.fetchLookup(
      newPath,
      newQuery,
      {},
      options.api.needProcess,
      options.api.method,
      newData,
      options.api.need_cache
    )

    return response
  } catch (error) {
    console.error('fetchLookup DropdownSearch', error)
  }
}

export default function () {
  return {
    parseOptions,
    processQuery,
    processPathQuery,
    fetchOptions
  }
}
